import { orpc } from '@/api/rpc/client';
import {
  AUTHZ_CACHE_TIME_MS,
  getAuthzQueryKey,
} from '@/context/authz/AuthzClient';
import { Action, Subject, type Permission } from '@erp/authz';
import { useQueries } from '@tanstack/react-query';
/**
 * Evaluate several permission checks at once, through the same per-permission
 * cache `useCan` uses, and get back a lookup. For surfaces gated on a list of
 * permissions that is data rather than code — the organization settings
 * sections, say — where one `useCan` per entry would have to be hand-listed
 * and silently drift.
 */
export function useCanMany(checks: readonly Permission[]): {
  can: (action: Action, subject: Subject) => boolean;
  isLoading: boolean;
} {
  return useQueries({
    queries: checks.map(({ action, subject }) =>
      orpc.authz.can.queryOptions({
        queryKey: getAuthzQueryKey({ action, subject }),
        staleTime: AUTHZ_CACHE_TIME_MS,
        gcTime: Infinity,
        input: { action, subject },
      }),
    ),
    combine: (results) => {
      const allowed = new Map(
        results.map((result, index) => [
          `${checks[index].action}:${checks[index].subject}`,
          Boolean(result.data?.allowed),
        ]),
      );
      return {
        can: (action: Action, subject: Subject) =>
          allowed.get(`${action}:${subject}`) ?? false,
        isLoading: results.some((result) => result.isLoading),
      };
    },
  });
}
